import React, { useState, useCallback, useEffect } from "react";
import { withTranslation } from "react-i18next";
import styles from "./Credentials.module.scss";
import QuestionIcon from "assets/images/question_mark.svg";
import CredentialsLicensingHelpGuide from "./CredentialsLicensingHelpGuide";
import { useAccountOwnerCommon } from "lib/Context/AccountOwner";
import EmployeeSelection from "./EmployeeSelection";
import {
  useCredentialLicenseNotificationPreference,
  useUpdateCredentialLicenseNotificationPreferenceMutation,
} from "repositories/user-repository";
import toast from "react-hot-toast";
import Loader from "components/Loader";
import { MoreTagsFunction } from "../../accountOwner/pages/AddEvent/addEvents.functions";
import InviteesPopup from "../../accountOwner/pages/EventDetails/components/Modals/InviteesPopup";
import NotAccessible from "accountOwner/pages/ManagerDashboard/components/NotAccessible";
import { useHistory } from "react-router-dom/cjs/react-router-dom.min";
import constants from "../../constants";
// Map reminder days to days before expiry
const reminderDaysMap = {
  day90: 90,
  day30: 30,
  day7: 7,
  onExpiry: 0,
};
const Toggle = ({ id, checked, onChange, label }) => (
  <label className={styles.toggle} htmlFor={id}>
    <input
      type="checkbox"
      id={id}
      checked={checked}
      onChange={onChange}
      aria-label={label}
    />
    <span className={styles.slider}></span>
  </label>
);

const CustomCheckbox = ({ label, checked, onChange }) => {
  return (
    <label className={"ch-checkbox"}>
      <input type="checkbox" checked={checked} onChange={onChange} />
      <span>{label}</span>
    </label>
  );
};

const NotificationPanel = ({
  title,
  id,
  isEnabled,
  onToggle,
  selectedMember,
  onSelectClick,
  selections,
  onSelectionsChange,
  t,
}) => {
  const [invitePopupOpen, setInvitePopupOpen] = useState(false);
  const [invitePopupPosition, setInvitePopupPosition] = useState({
    top: 0,
    right: 0,
  });
  const handleCheck = (key) => {
    onSelectionsChange(id, key);
  };
  const handlePopupToggle = (event, setter) => {
    event.stopPropagation();
    setInvitePopupPosition({
      top: 40,
      left: 40,
    });
    setter((prev) => !prev);
  };

  return (
    <div className={`${styles.panel} ${isEnabled ? styles.expanded : ""}`}>
      <div className={styles.panel__header}>
        <h3>{title}</h3>
        <Toggle
          id={`toggle-${id}`}
          checked={isEnabled}
          onChange={() => onToggle(id)}
          label={`Toggle ${title}`}
        />
      </div>
      <div className={styles.panel__bodyWrapper} aria-hidden={!isEnabled}>
        <div className={styles.panel__body}>
          <div className={styles.optionsList}>
            <CustomCheckbox
              label={t("accountOwner.ninetyDaysBeforeExpiry")}
              checked={selections.day90.isEnabled}
              onChange={() => handleCheck("day90")}
            />
            <CustomCheckbox
              label={t("accountOwner.thirtyDaysBeforeExpiry")}
              checked={selections.day30.isEnabled}
              onChange={() => handleCheck("day30")}
            />
            <CustomCheckbox
              label={t("accountOwner.sevenDaysBeforeExpiry")}
              checked={selections.day7.isEnabled}
              onChange={() => handleCheck("day7")}
            />
            <CustomCheckbox
              label={t("accountOwner.onExpiry")}
              checked={selections.onExpiry.isEnabled}
              onChange={() => handleCheck("onExpiry")}
            />
          </div>

          <div className={styles.selectionRow + " position-relative"}>
            <div className={styles.labelGroup}>
              <span>{t("accountOwner.notifyStaff")}</span>
              <strong>
                {selectedMember?.length === 0 ? (
                  <span>{t("accountOwner.noStaffSelected")}</span>
                ) : (
                  <MoreTagsFunction
                    value={selectedMember}
                    parentClass={styles.moreTagsFunctionParent}
                    styles={styles.link_new}
                    clickHandler={(e) =>
                      handlePopupToggle(e, setInvitePopupOpen)
                    }
                    mapFn={(member) =>
                      member?.userName ||
                      [member?.user?.firstName, member?.user?.lastName]
                        .filter(Boolean)
                        .join(" ")
                    }
                  />
                )}
              </strong>
              {invitePopupOpen && (
                <InviteesPopup
                  position={invitePopupPosition}
                  handleInvitePopupToggle={(e) =>
                    handlePopupToggle(e, setInvitePopupOpen)
                  }
                  list={selectedMember}
                  showRoleAndOffice={true}
                  isForEvent={true}
                  parentClass={styles.invitePopupParent}
                  listClass={styles.invitePopupListClass}
                />
              )}
            </div>
            <button
              type="button"
              className={styles.selectAction}
              onClick={onSelectClick}
            >
              {t("accountOwner.select")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const CredentialsAndLicensing = ({ t }) => {
  const history = useHistory();
  const { selectedId, isStaff, dataAccessValues, isOwner,commonOwnerData:selectedOwner } =
    useAccountOwnerCommon();

  const canAccessThisPage = isOwner || (isStaff && dataAccessValues.isAdmin);
  const cannotAccessThisPage = !canAccessThisPage;

  // Fetch notification preferences
  const {
    data: preferenceData,
    isLoading: isLoadingPreferences,
    isFetching: isFetchingPreferences,
    refetch: refetchPreferences,
  } = useCredentialLicenseNotificationPreference(selectedId, {
    enabled: !!selectedId,
  });

  // Update mutation
  const updateMutation =
    useUpdateCredentialLicenseNotificationPreferenceMutation();

  const [panelStates, setPanelStates] = useState({
    staff: false,
    office: false,
  });

  const [credentialTimings, setCredentialTimings] = useState({
    staff: {
      day90: { id: null, isEnabled: false },
      day30: { id: null, isEnabled: false },
      day7: { id: null, isEnabled: false },
      onExpiry: { id: null, isEnabled: false },
    },
    office: {
      day90: { id: null, isEnabled: false },
      day30: { id: null, isEnabled: false },
      day7: { id: null, isEnabled: false },
      onExpiry: { id: null, isEnabled: false },
    },
  });

  // Modal states
  const [isSelectStaffMemberModalOpen, setIsSelectStaffMemberModalOpen] =
    useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);

  // Selected members states - categorized by type
  // Staff members: store both full objects and IDs
  const [selectedStaffMemberIds, setSelectedStaffMemberIds] = useState({
    staff: [],
    office: [],
  });

  // Initialize state from fetched data
  useEffect(() => {
    if (preferenceData) {
      // Set panel states
      setPanelStates({
        staff: preferenceData.isUserCredentialEnabled ?? true,
        office: preferenceData.isOfficeCredentialEnabled ?? true,
      });

      // Map credential timings
      if (preferenceData.notificationTimings?.length) {
        const timings = { staff: {}, office: {} };

        preferenceData.notificationTimings.forEach((timing) => {
          const ownerType =
            timing.credentialOwnerType === 1 ? "staff" : "office";
          const reminderDays = timing.reminderDaysBefore;

          // Find the key based on reminder days
          let key = "onExpiry";
          if (reminderDays === 90) key = "day90";
          else if (reminderDays === 30) key = "day30";
          else if (reminderDays === 7) key = "day7";

          timings[ownerType][key] = {
            id: timing.id,
            isEnabled: timing.isEnabled ?? false,
          };
        });

        // Fill in missing keys with defaults
        ["staff", "office"].forEach((type) => {
          ["day90", "day30", "day7", "onExpiry"].forEach((key) => {
            if (!timings[type][key]) {
              timings[type][key] = { id: null, isEnabled: false };
            }
          });
        });

        setCredentialTimings(timings);
      }

      // Map notification users
      if (preferenceData.notificationUsers?.length) {
        setSelectedStaffMemberIds(
          preferenceData.notificationUsers.reduce(
            (acc, res) => {
              acc[res.credentialOwnerType === 1 ? "staff" : "office"].push(res);
              return acc;
            },
            { staff: [], office: [] },
          ),
        );
      }
    }
  }, [preferenceData]);

  const handleToggle = useCallback((id) => {
    setPanelStates((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  }, []);

  // Handle credential timing selections change
  const handleSelectionsChange = useCallback((panelId, key) => {
    setCredentialTimings((prev) => {
      return {
        ...prev,
        [panelId]: {
          ...prev[panelId],
          [key]: {
            ...prev[panelId][key],
            isEnabled: !prev[panelId][key].isEnabled,
          },
        },
      };
    });
  }, []);
  // Handle save button click
  const handleSave = useCallback(async () => {
    try {
      // Build CredentialTimings array
      const credentialTimingsPayload = [];

      ["staff", "office"].forEach((panelType) => {
        const ownerType = panelType === "staff" ? 1 : 2;
        Object.entries(credentialTimings[panelType]).forEach(
          ([key, timing]) => {
            if (timing.id || timing.isEnabled) {
              credentialTimingsPayload.push({
                id: timing.id,
                credentialOwnerType: ownerType,
                reminderDaysBefore: reminderDaysMap[key],
                isEnabled: timing.isEnabled,
              });
            }
          },
        );
      });

      // Build notificationUsers array
      const notificationUsersPayload = [];
      const rememberUsers = new Map();
      // Add existing users that should be deleted (from API but not in current selection)
      if (preferenceData?.notificationUsers) {
        preferenceData.notificationUsers.forEach((user) => {
          const ownerType = user.credentialOwnerType === 1 ? "staff" : "office";
          rememberUsers.set(`${user.userId} ${ownerType}`, ownerType);
          const isInCurrentSelection = selectedStaffMemberIds[ownerType].some(
            (current) => current.userId === user.userId,
          );
          notificationUsersPayload.push({
            id: user.id,
            userId: user.userId,
            credentialOwnerType: user.credentialOwnerType,
            IsDeleted: !isInCurrentSelection,
          });
        });
      }
      Object.entries(selectedStaffMemberIds).forEach(([ownerType, users]) => {
        users.forEach((user) => {
          const existingUser = rememberUsers.has(`${user.userId} ${ownerType}`);
          if (!existingUser) {
            notificationUsersPayload.push({
              id: 0,
              userId: user.userId,
              credentialOwnerType: user.credentialOwnerType,
              IsDeleted: false,
            });
          }
        });
      });

      const payload = {
        OwnerId: selectedId,
        IsUserCredentialEnabled: panelStates.staff,
        IsOfficeCredentialEnabled: panelStates.office,
        CredentialTimings: credentialTimingsPayload,
        notificationUsers: notificationUsersPayload,
      };
      await updateMutation.mutateAsync(payload);
      toast.success(t("accountOwner.preferencesSavedSuccessfully"));
      refetchPreferences();
    } catch (error) {
      toast.error(error?.message);
    }
  }, [
    selectedId,
    panelStates,
    credentialTimings,
    selectedStaffMemberIds,
    preferenceData,
    updateMutation,
    refetchPreferences,
  ]);

  // Handle opening modals based on panel type
  const handleSelectClick = useCallback((panelId) => {
    setIsSelectStaffMemberModalOpen(panelId);
  }, []);
  const goToPreviousScreen = () => {
    if (window.history.length > 1) {
      history.goBack();
    } else {
      history.push(constants.routes.accountPreferences);
    }
  };

  const isLoading = isLoadingPreferences || isFetchingPreferences || updateMutation.isLoading;

  return (
    <div className={styles.pageContainer}>
      <div className={styles.backBtnContainer}>
        <button className="back-btn" onClick={() => history.goBack()}>
          <div className="back-btn-text">
            <span className="ico">
              <img
                src={require("assets/images/arrow-back-icon.svg").default}
                alt="arrow"
              />
            </span>
            {t("back")}
          </div>
        </button>
        <div className={styles.pageHeaderRow}>
          <h2 className={`page-title mb-0 ${styles.pageHeaderTitle}`}>
            {t("accountOwner.credentialsAndLicensing")}
          </h2>
          <button
            type="button"
            className={styles.credentialsHelpBtn}
            onClick={() => setIsHelpOpen(true)}
            aria-label={t("navbar.help")}
          >
            <img src={QuestionIcon} alt="" />
            <span className="m-0">{t("navbar.help")}</span>
          </button>
        </div>
{isStaff&& selectedOwner?.id&&        <div className="show-owner-name-text">
              {t("officePolicyManagement.showCredentialsAndLicensing")}{" "}
              <span>
                '{selectedOwner.firstName} {selectedOwner.lastName}'
              </span>
            </div>}
      </div>
      {isLoading && <Loader />}
      {cannotAccessThisPage ? (
        <div
          className="w-100 not-accessible center_property"
          style={{ height: "300px" }}
        >
          <NotAccessible Message={t("dashBoard.notAccessible")} />
        </div>
      ) : (
        <main className={styles.card}>
          <NotificationPanel
            id="staff"
            title={t("accountOwner.manageStaffCredentialExpiryReminders")}
            isEnabled={panelStates.staff}
            onToggle={handleToggle}
            onSelectClick={() => handleSelectClick("staff")}
            selectedMember={selectedStaffMemberIds.staff}
            selections={credentialTimings.staff}
            onSelectionsChange={handleSelectionsChange}
            t={t}
          />

          <NotificationPanel
            id="office"
            title={t("accountOwner.manageOfficeCredentialExpiryReminders")}
            isEnabled={panelStates.office}
            onToggle={handleToggle}
            onSelectClick={() => handleSelectClick("office")}
            selectedMember={selectedStaffMemberIds.office}
            selections={credentialTimings.office}
            onSelectionsChange={handleSelectionsChange}
            t={t}
          />
          <div className={`d-flex gap-xs  ${styles["credentials-cta-box"]}`}>
            <button
              className={styles.saveBtn}
              onClick={handleSave}
              disabled={isLoading}
            >
              {isLoading ? t("accountOwner.saving") : t("save")}
            </button>
            <button
              className="button button-round button-border btn-mobile-link button-dark mb-md-3"
              onClick={() => history.goBack()}
              title={t("cancel")}
            >
              {t("cancel")}
            </button>
          </div>
        </main>
      )}

      {isHelpOpen && (
        <CredentialsLicensingHelpGuide
          isOpen={isHelpOpen}
          onClose={setIsHelpOpen}
        />
      )}

      {isSelectStaffMemberModalOpen && (
        <EmployeeSelection
          issaveEmployeeModalOpen={isSelectStaffMemberModalOpen}
          setIsSaveEmployeeModalOpen={setIsSelectStaffMemberModalOpen}
          memberIds={selectedStaffMemberIds[isSelectStaffMemberModalOpen]}
          setMemberIds={(ids) =>
            setSelectedStaffMemberIds({
              ...selectedStaffMemberIds,
              [isSelectStaffMemberModalOpen]: ids.map((res) => ({
                ...res,
                credentialOwnerType:
                  isSelectStaffMemberModalOpen === "staff" ? 1 : 2,
              })),
            })
          }
          ownerId={selectedId}
        />
      )}
    </div>
  );
};

export default withTranslation()(CredentialsAndLicensing);
